import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { pushSupported, isIOS, isStandalonePWA } from "@/lib/platform";

/**
 * Handles the Web Push subscription lifecycle. Runs quietly in the background:
 * - Fetches the server's VAPID public key
 * - Ensures the browser has an active push subscription
 * - Registers it with the backend so it can send us reply / room-activity pings
 *
 * Never prompts on its own — permission is requested via the explicit
 * "Enable notifications" button on the Profile page.
 *
 * On iOS 16.4+, push only works inside an installed PWA (Add to Home Screen).
 * The hook silently no-ops when running in normal Safari.
 */

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureSubscription() {
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/vapid-public-key");
    if (!data?.public_key) throw new Error("no-key");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return sub;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const doneRef = useRef(false);

  useEffect(() => {
    if (!user || user === false || user.is_guest || doneRef.current) return;
    if (!pushSupported()) return;
    if (Notification.permission !== "granted") return;

    doneRef.current = true;
    ensureSubscription().catch(() => { /* silent — nice-to-have */ });
  }, [user]);
}

/**
 * Explicit opt-in handler. MUST be called from a user gesture (button click)
 * so iOS 16.4+ permits the permission prompt.
 *
 * IMPORTANT: On iOS the `Notification.requestPermission()` call has to happen
 * synchronously in the same task as the tap — do NOT await anything before it.
 */
export async function enablePushNotifications() {
  if (!pushSupported()) {
    // Give the UI enough info to render the right instruction card.
    if (isIOS() && !isStandalonePWA()) return { ok: false, reason: "ios-needs-install" };
    return { ok: false, reason: "unsupported" };
  }
  if (Notification.permission === "denied") {
    return { ok: false, reason: "denied" };
  }
  // Fire the permission prompt FIRST — nothing awaited before it so we stay
  // inside the user-gesture context that iOS Safari requires.
  let permResult;
  try {
    permResult = Notification.requestPermission();
    // Older API returned undefined and used a callback; wrap for safety.
    if (permResult && typeof permResult.then === "function") {
      permResult = await permResult;
    } else {
      permResult = await new Promise((resolve) => {
        try { Notification.requestPermission(resolve); }
        catch { resolve(Notification.permission); }
      });
    }
  } catch (e) {
    return { ok: false, reason: "prompt-failed" };
  }
  if (permResult !== "granted") {
    return { ok: false, reason: permResult || "denied" };
  }
  try {
    await ensureSubscription();
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "subscribe-failed" };
  }
}
